import { useState, useRef } from "react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  FileText, Upload, Link2, StickyNote, Loader2,
  CheckCircle, X, File, Image, Video, FileIcon, Plus,
} from "lucide-react";
import { toast } from "sonner";

const API = window.location.origin;

function FilePreview({ file, onRemove }) {
  const isImage = file.type?.startsWith("image/");
  const isVideo = file.type?.startsWith("video/");
  const sizeKB = (file.size / 1024).toFixed(0);
  const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
  const sizeLabel = file.size > 1024 * 1024 ? `${sizeMB} MB` : `${sizeKB} KB`;

  return (
    <div className="flex items-center gap-2 bg-bone rounded-sm border border-bone-dark p-2" data-testid={`file-preview-${file.name}`}>
      <div className="w-8 h-8 bg-navy/10 rounded flex items-center justify-center shrink-0">
        {isImage ? <Image className="h-4 w-4 text-navy" /> :
         isVideo ? <Video className="h-4 w-4 text-navy" /> :
         <FileIcon className="h-4 w-4 text-navy" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-navy text-xs font-medium truncate">{file.name}</p>
        <p className="text-gray-400 text-[10px]">{sizeLabel}</p>
      </div>
      <button onClick={onRemove} className="text-gray-300 hover:text-red-400 transition-colors shrink-0" data-testid={`remove-file-${file.name}`}>
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export default function PageRequestPage() {
  const [description, setDescription] = useState("");
  const [websiteLinks, setWebsiteLinks] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef(null);

  const handleFiles = (e) => {
    const newFiles = Array.from(e.target.files || []);
    setFiles(prev => [...prev, ...newFiles]);
    e.target.value = "";
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) {
      toast.error("Please provide a description of the page you'd like created.");
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("description", description.trim());
      formData.append("website_links", websiteLinks);
      formData.append("additional_notes", additionalNotes);
      formData.append("submitter_name", name);
      formData.append("submitter_email", email);
      for (const f of files) {
        formData.append("attachments", f);
      }

      const res = await fetch(`${API}/api/page-request`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Submission failed");
      }

      setSubmitted(true);
      toast.success("Your page request has been submitted!");
    } catch (err) {
      toast.error(err.message || "Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <section className="bg-navy py-10 text-white">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
            <div className="h-px w-10 bg-gold mx-auto mb-3" />
            <h1 className="font-heading text-3xl sm:text-4xl">Request Submitted</h1>
          </div>
        </section>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 text-center" data-testid="request-success">
          <div className="w-16 h-16 bg-green-50 border-2 border-green-200 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="font-heading text-2xl text-navy mb-3">Thank You!</h2>
          <p className="text-gray-600 text-base leading-relaxed mb-2">
            Your page creation request has been submitted successfully.
          </p>
          <p className="text-gray-500 text-sm mb-6">
            Our team will review your request and get back to you soon. If you included your email, we'll reach out with any questions.
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              className="btn-gold"
              onClick={() => {
                setSubmitted(false);
                setDescription("");
                setWebsiteLinks("");
                setAdditionalNotes("");
                setFiles([]);
              }}
              data-testid="submit-another-btn"
            >
              Submit Another Request
            </Button>
            <a href="/">
              <Button variant="outline" className="border-navy/20 text-navy rounded-sm">
                Back to Home
              </Button>
            </a>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <section className="bg-navy py-10 text-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="h-px w-10 bg-gold mb-3" />
          <h1 className="font-heading text-3xl sm:text-4xl mb-2">Request a New Page</h1>
          <p className="text-white/60 text-sm leading-relaxed">
            Need a custom page for your ministry, program, or event? Fill out the form below and our team will create it for you.
          </p>
        </div>
      </section>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="page-request-form">

          {/* Contact Info (optional) */}
          <Card className="shadow-sm">
            <CardContent className="p-5 space-y-4">
              <h3 className="font-heading text-lg text-navy">Your Information (Optional)</h3>
              <p className="text-gray-500 text-xs">So we can follow up if we have questions.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-navy text-sm font-medium">Name</Label>
                  <Input
                    data-testid="request-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="mt-1 border-bone-dark focus:border-gold rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-navy text-sm font-medium">Email</Label>
                  <Input
                    data-testid="request-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email address"
                    type="email"
                    className="mt-1 border-bone-dark focus:border-gold rounded-sm"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Description (Required) */}
          <Card className="shadow-sm border-gold/30">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-gold" />
                <h3 className="font-heading text-lg text-navy">Page Description</h3>
                <span className="text-red-500 text-xs">*Required</span>
              </div>
              <p className="text-gray-500 text-xs leading-relaxed">
                Describe the page you'd like created. Include the purpose, content outline, sections, and any specific requirements.
              </p>
              <Textarea
                data-testid="request-description-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Example: We need a page for our Women's Ministry called 'Sisters in Faith'. It should include a welcome section, our mission statement, weekly meeting times (Tuesdays at 6:30 PM), upcoming retreat details, and a signup form..."
                rows={6}
                className="border-bone-dark focus:border-gold rounded-sm resize-none"
                required
              />
            </CardContent>
          </Card>

          {/* Upload Attachments */}
          <Card className="shadow-sm">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center gap-2">
                <Upload className="h-5 w-5 text-gold" />
                <h3 className="font-heading text-lg text-navy">Upload Attachments</h3>
              </div>
              <p className="text-gray-500 text-xs leading-relaxed">
                Upload photos, images, videos, documents, or other files you'd like included on the page.
              </p>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*,.pdf,.doc,.docx,.txt,.pptx,.xlsx"
                onChange={handleFiles}
                className="hidden"
                data-testid="file-upload-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-bone-dark hover:border-gold/50 rounded-sm p-6 text-center transition-colors group"
                data-testid="file-upload-area"
              >
                <Upload className="h-8 w-8 text-gray-300 group-hover:text-gold mx-auto mb-2 transition-colors" />
                <p className="text-navy text-sm font-medium">Click to upload files</p>
                <p className="text-gray-400 text-xs mt-1">Images, videos, PDFs, documents (multiple allowed)</p>
              </button>

              {files.length > 0 && (
                <div className="space-y-1.5" data-testid="file-list">
                  {files.map((f, i) => (
                    <FilePreview key={`${f.name}-${i}`} file={f} onRemove={() => removeFile(i)} />
                  ))}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-1.5 text-gold text-xs hover:underline mt-1"
                    data-testid="add-more-files-btn"
                  >
                    <Plus className="h-3 w-3" /> Add more files
                  </button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Website Links */}
          <Card className="shadow-sm">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center gap-2">
                <Link2 className="h-5 w-5 text-gold" />
                <h3 className="font-heading text-lg text-navy">Website Links</h3>
              </div>
              <p className="text-gray-500 text-xs leading-relaxed">
                Add URLs to external photos, videos, reference pages, or any other online resources you'd like us to use. One link per line.
              </p>
              <Textarea
                data-testid="request-links-input"
                value={websiteLinks}
                onChange={(e) => setWebsiteLinks(e.target.value)}
                placeholder={"https://example.com/photo.jpg\nhttps://youtube.com/watch?v=...\nhttps://reference-page.com"}
                rows={3}
                className="border-bone-dark focus:border-gold rounded-sm resize-none font-mono text-xs"
              />
            </CardContent>
          </Card>

          {/* Additional Notes */}
          <Card className="shadow-sm">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center gap-2">
                <StickyNote className="h-5 w-5 text-gold" />
                <h3 className="font-heading text-lg text-navy">Additional Notes</h3>
              </div>
              <p className="text-gray-500 text-xs leading-relaxed">
                Any extra details, ideas, style preferences, or special instructions.
              </p>
              <Textarea
                data-testid="request-notes-input"
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                placeholder="E.g., Use a warm, welcoming tone. Match the color scheme of our main site. Include a call-to-action button for signups..."
                rows={3}
                className="border-bone-dark focus:border-gold rounded-sm resize-none"
              />
            </CardContent>
          </Card>

          {/* Submit */}
          <Button
            type="submit"
            disabled={submitting || !description.trim()}
            className="w-full btn-gold h-12 text-base font-semibold"
            data-testid="submit-page-request-btn"
          >
            {submitting ? (
              <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Submitting Request...</>
            ) : (
              <><FileText className="h-5 w-5 mr-2" /> Submit Page Request</>
            )}
          </Button>

          <p className="text-center text-gray-400 text-xs">
            Your request will be sent to our web team for review and creation.
          </p>
        </form>
      </div>
    </Layout>
  );
}
